import type {
  TransactionImportPreviewRowDTO,
  TransactionImportRowOverrideDTO,
} from '@inversor-hub/types';

export type ImportRowStatus =
  | 'ready'
  | 'needs_review'
  | 'blocked'
  | 'possible_duplicate'
  | 'skipped';

export type PreviewFilter =
  | 'all'
  | 'importable'
  | 'with_warnings'
  | 'blocked'
  | 'skipped'
  | 'duplicates';

export interface PreviewSummary {
  total: number;
  importable: number;
  withWarnings: number;
  blocked: number;
  skipped: number;
  duplicates: number;
}

export function isImportableStatus(status: ImportRowStatus, includeDuplicates: boolean): boolean {
  return (
    status === 'ready' ||
    status === 'needs_review' ||
    (status === 'possible_duplicate' && includeDuplicates)
  );
}

const CATEGORY_CONFIDENCE_ERRORS = [
  'Categoría no clasificada por IA',
  'Categoría con baja confianza: requiere revisión manual',
];

export function derivePreviewStatus(
  row: TransactionImportPreviewRowDTO,
  override: TransactionImportRowOverrideDTO | undefined
): ImportRowStatus {
  if (override?.skip) return 'skipped';
  const hasResolvedCategory = Boolean(override?.category) || Boolean(override?.lines?.length);
  const nonDuplicateErrors = row.errors.filter((error) => {
    if (error === 'Duplicado omitido') return false;
    if (error === 'Inversión no identificada: requiere revisión manual' && override?.investmentId) {
      return false;
    }
    // Confirming or changing the category resolves these locally, so the row reflects
    // the fix immediately instead of staying blocked until the next preview refresh.
    if (hasResolvedCategory && CATEGORY_CONFIDENCE_ERRORS.includes(error)) {
      return false;
    }
    return true;
  });
  if (nonDuplicateErrors.length > 0) {
    return nonDuplicateErrors.every(isPreviewCorrectableError) ? 'blocked' : 'skipped';
  }

  if (row.duplicate) return 'possible_duplicate';

  const unresolvedMediumCategory =
    row.draft.categoryConfidence === 'MEDIUM' && !hasResolvedCategory;
  if (unresolvedMediumCategory || row.warnings.length > 0) return 'needs_review';
  return 'ready';
}

function isPreviewCorrectableError(error: string): boolean {
  return [
    'Concepto obligatorio',
    'Categoría no clasificada por IA',
    'Categoría con baja confianza: requiere revisión manual',
    'Inversión no identificada: requiere revisión manual',
  ].includes(error);
}

export function matchesFilter(
  filter: PreviewFilter,
  row: TransactionImportPreviewRowDTO,
  status: ImportRowStatus,
  includeDuplicates = false
): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'importable':
      return isImportableStatus(status, includeDuplicates);
    case 'with_warnings':
      return status === 'needs_review';
    case 'blocked':
      return status === 'blocked';
    case 'skipped':
      return status === 'skipped';
    case 'duplicates':
      return row.duplicate;
  }
}
